import React, { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Rpf26SmartInput } from "./Rpf26SmartInput";
import { Rpf26EngineWorkspace, validateRpf26Frontespizio, validateRpf26LmRs } from "./Rpf26EngineWorkspace";

// Generic schema-driven form renderer for pratica modules.
// Supports two schema styles:
// 1. Local JS schemas (formSchemas.js): sections with fields[{path, showIf: fn}]
// 2. Remote declarative schemas (backend /pratiche/modules/{code}/schema):
//    sections with {type: "single"|"list", path, fields|columns[{key,...}]}
export const inputCls = "w-full px-3 py-2 rounded-md border border-border bg-card focus:border-primary focus:ring-2 focus:ring-primary/20 outline-none text-sm";

function getPath(obj, path) {
    if (!path) return obj;
    return path.split(".").reduce((cur, k) => (cur == null ? undefined : cur[k]), obj);
}

function evalShowIf(showIf, form) {
    if (!showIf) return true;
    if (typeof showIf === "function") return showIf(form);
    const v = getPath(form, showIf.field);
    if (showIf.equals !== undefined) return v === showIf.equals;
    if (showIf.in) return showIf.in.includes(v);
    return true;
}

export function ModuleForm({ schema, form, editing, updField, errors = [], telematicProfile = {} }) {
    if (!schema) return null;
    const sections = Array.isArray(schema.sections) ? schema.sections : [];
    if (schema.module_code === "REDDITI_PF") {
        return <Rpf26ModuleForm sections={sections} form={form} editing={editing} updField={updField} errors={errors} telematicProfile={telematicProfile} />;
    }
    return (
        <>
            {sections
                .filter((s) => evalShowIf(s.showIf, form))
                .map((section) =>
                    section.type === "list" ? (
                        <ListSection key={section.code || section.title} section={section} form={form} editing={editing} updField={updField} />
                    ) : (
                        <SingleSection key={section.code || section.title} section={section} form={form} editing={editing} updField={updField} />
                    )
                )}
        </>
    );
}

const RPF26_FRONT_CODES = new Set(["FRONT", "FRONT-DATI", "FRONT-RECAPITI", "FRONT-RES", "FRONT-DOM25", "FRONT-DOM26", "FRONT-ESTERO", "FRONT-RAPP", "FRONT-SCELTE", "FRONT-FIRMA"]);
const RPF26_LM_CODES = new Set(["LM", "LM-Q", "LM22-27", "LM40"]);
const RPF26_RS_CODES = new Set(["RS-LM"]);
const RPF26_RN_CODES = new Set(["RN", "RN47", "RN50"]);
const RPF26_RV_CODES = new Set(["RV"]);
const RPF26_LC_CODES = new Set(["LC"]);
const RPF26_RX_CODES = new Set(["RX"]);

export const RPF26_OFFICIAL_WORKSPACES = [
    ["FRONT", "Frontespizio", "Anagrafica, residenza e scelte"],
    ["RA", "Quadro RA", "Redditi dei terreni"],
    ["RB", "Quadro RB", "Redditi dei fabbricati"],
    ["RC", "Quadro RC", "Lavoro dipendente e pensione"],
    ["FAM", "Familiari", "Familiari a carico"],
    ["CR", "Quadro CR", "Crediti d’imposta"],
    ["RP", "Quadro RP", "Oneri e spese"],
    ["RL", "Quadro RL", "Altri redditi"],
    ["RM", "Quadro RM", "Tassazione separata"],
    ["RT", "Quadro RT", "Plusvalenze e imposte sostitutive"],
    ["RR", "Quadro RR", "Contributi previdenziali"],
    ["RW", "Quadro RW", "Investimenti e attività estere"],
    ["AC", "Quadro AC", "Amministratori di condominio"],
    ["RD", "Quadro RD", "Allevamento"],
    ["CP", "Quadro CP", "Concordato preventivo biennale"],
    ["RE", "Quadro RE", "Lavoro autonomo"],
    ["RF", "Quadro RF", "Impresa in contabilità ordinaria"],
    ["RG", "Quadro RG", "Impresa in contabilità semplificata"],
    ["RH", "Quadro RH", "Redditi di partecipazione"],
    ["LM", "Quadro LM", "Vantaggio e regime forfetario"],
    ["RS", "Quadro RS", "Prospetti vari"],
    ["RU", "Quadro RU", "Crediti d’imposta"],
    ["RQ", "Quadro RQ", "Imposte sostitutive"],
    ["FC", "Quadro FC", "Soggetti controllati esteri"],
    ["CE", "Quadro CE", "Crediti per redditi esteri"],
    ["NR", "Quadro NR", "Nuovi residenti"],
    ["TR", "Quadro TR", "Imposizione in uscita e ingresso"],
    ["RN", "Quadro RN", "Determinazione dell’IRPEF"],
    ["RV", "Quadro RV", "Addizionali regionali e comunali"],
    ["LC", "Quadro LC", "Cedolare secca sulle locazioni"],
    ["DI", "Quadro DI", "Dichiarazione integrativa"],
    ["RX", "Quadro RX", "Risultato della dichiarazione"],
];

export function sectionsForRpf26Workspace(sections, code) {
    return sections.filter((section) => {
        const sectionCode = String(section.code || "").toUpperCase();
        const sectionPath = String(section.path || "").toUpperCase();
        return sectionCode === code
            || sectionCode.startsWith(`${code}_`)
            || sectionCode.startsWith(`${code}-`)
            || new RegExp(`^${code}\\d`).test(sectionCode)
            || sectionPath === `QUADRI.${code}`
            || sectionPath.startsWith(`QUADRI.${code}.`);
    });
}

const RPF26_RN_BANDS = [
    { code: "RN1_RN5", title: "Reddito e imposta lorda", subtitle: "RN1–RN5", min: 1, max: 5 },
    { code: "RN6_RN23", title: "Detrazioni e imposta netta", subtitle: "RN6–RN23", min: 6, max: 23 },
    { code: "RN24_RN32", title: "Crediti d’imposta", subtitle: "RN24–RN32", min: 24, max: 32 },
    { code: "RN33_RN46", title: "Ritenute e liquidazione", subtitle: "RN33–RN46", min: 33, max: 46 },
    { code: "RN47_RN50", title: "Residui e altri dati", subtitle: "RN47 e RN50", min: 47, max: 50 },
    { code: "RN61_RN62", title: "Acconti", subtitle: "RN61–RN62", min: 61, max: 62 },
];

export function partitionRpf26Sections(sections, form) {
    const visible = sections.filter((section) => evalShowIf(section.showIf, form));
    return {
        visible,
        rn: visible.filter((section) => RPF26_RN_CODES.has(section.code)),
        rv: visible.filter((section) => RPF26_RV_CODES.has(section.code)),
        lc: visible.filter((section) => RPF26_LC_CODES.has(section.code)),
        rx: visible.filter((section) => RPF26_RX_CODES.has(section.code)),
        other: visible.filter((section) =>
            !RPF26_FRONT_CODES.has(section.code)
            && !RPF26_LM_CODES.has(section.code)
            && !RPF26_RS_CODES.has(section.code)
            && !RPF26_RN_CODES.has(section.code)
            && !RPF26_RV_CODES.has(section.code)
            && !RPF26_LC_CODES.has(section.code)
            && !RPF26_RX_CODES.has(section.code)
        ),
    };
}

export function groupRpf26RnFields(sections) {
    const fields = sections.flatMap((section) => (section.fields || []).map((field) => ({
        ...field,
        path: fieldPath(section, field),
        sectionCode: section.code,
    })));
    const officialFields = fields.filter((field) => field.rn_workspace_role === "official" && field.official_code);
    const supportFields = fields.filter((field) => field.rn_workspace_role === "support");
    const rows = new Map();
    officialFields.forEach((field) => {
        const rowNumber = Number(String(field.official_row || "").replace("RN", ""));
        if (!rows.has(rowNumber)) rows.set(rowNumber, []);
        rows.get(rowNumber).push(field);
    });
    const bands = RPF26_RN_BANDS.map((band) => ({
        ...band,
        rows: [...rows.entries()]
            .filter(([rowNumber]) => rowNumber >= band.min && rowNumber <= band.max)
            .sort(([left], [right]) => left - right)
            .map(([rowNumber, rowFields]) => ({
                code: `RN${rowNumber}`,
                rowNumber,
                fields: rowFields.sort((left, right) => Number(left.official_column) - Number(right.official_column)),
            })),
    }));
    return {
        bands,
        supportFields,
        officialCount: officialFields.length,
        engineCount: officialFields.filter((field) => field.field_owner === "fiscal_engine").length,
        documentInputCount: officialFields.filter((field) => field.field_owner === "document_input").length,
    };
}

function renderSchemaSection(section, form, editing, updField) {
    return section.type === "list" ? (
        <ListSection key={section.code || section.title} section={section} form={form} editing={editing} updField={updField} />
    ) : (
        <SingleSection key={section.code || section.title} section={section} form={form} editing={editing} updField={updField} />
    );
}

function Rpf26ModuleForm({ sections, form, editing, updField, errors, telematicProfile }) {
    const [active, setActive] = useState(() => window.localStorage.getItem("fiscox-rpf26-workspace") || "FRONT");
    const [localFrontErrors, setLocalFrontErrors] = useState([]);
    const [localLmRsErrors, setLocalLmRsErrors] = useState([]);
    const tabsScrollerRef = useRef(null);
    const tabRefs = useRef({});
    useEffect(() => {
        const openFrontespizio = () => setActive("FRONT");
        window.addEventListener("fiscox:rpf26-open-frontespizio", openFrontespizio);
        const openWorkspace = (event) => setActive(event.detail === "RS" ? "RS" : event.detail === "LM" ? "LM" : "FRONT");
        window.addEventListener("fiscox:rpf26-open-workspace", openWorkspace);
        return () => { window.removeEventListener("fiscox:rpf26-open-frontespizio", openFrontespizio); window.removeEventListener("fiscox:rpf26-open-workspace", openWorkspace); };
    }, []);
    const { rn, rv, lc, rx, other } = partitionRpf26Sections(sections, form);
    const select = (code) => {
        if (active === "FRONT" && code !== "FRONT") {
            const nextErrors = validateRpf26Frontespizio(form, telematicProfile);
            if (nextErrors.length) {
                setLocalFrontErrors(nextErrors);
                toast.error(`Frontespizio incompleto: ${nextErrors.length} campi obbligatori da compilare.`);
            }
        }
        if (["LM", "RS"].includes(active) && code !== active) {
            const nextErrors = validateRpf26LmRs(form);
            if (nextErrors.length) { setLocalLmRsErrors(nextErrors); toast.error(`Quadri LM/RS incompleti: ${nextErrors.length} controlli da completare prima dell’invio.`); }
        }
        if (code === "FRONT") setLocalFrontErrors([]);
        setActive(code);
        window.localStorage.setItem("fiscox-rpf26-workspace", code);
    };
    const serverFrontErrors = errors
        .filter((error) => String(error.field || "").startsWith("frontespizio."))
        .map((error) => ({ ...error, controlId: Object.entries({
            "frontespizio.codice_fiscale": "fiscalCode", "frontespizio.cognome": "lastName", "frontespizio.nome": "firstName",
            "frontespizio.provincia_nascita": "birthProvince", "frontespizio.comune_nascita": "birthPlace", "frontespizio.data_nascita": "birthDate",
            "frontespizio.domicilio_fiscale_comune": "domicile-2025-municipality", "frontespizio.domicilio_fiscale_provincia": "domicile-2025-province",
            "frontespizio.domicilio_fiscale_codice_comune": "domicile-2025-cadastral-code", "frontespizio.sesso": "sex",
            "frontespizio.scelta_otto_per_mille": "eight-recipient", "frontespizio.scelta_otto_stato_finalita": "eight-state-purpose",
            "frontespizio.scelta_cinque_per_mille": "five-purpose", "frontespizio.scelta_due_per_mille_partito": "two-party",
        }).find(([field]) => field === error.field)?.[1] }));
    useEffect(() => {
        if (!serverFrontErrors.length) return;
        setActive("FRONT");
        window.localStorage.setItem("fiscox-rpf26-workspace", "FRONT");
    }, [serverFrontErrors.length]);
    const requestedFrontValidation = localFrontErrors.length ? localFrontErrors : serverFrontErrors;
    const frontValidationErrors = (telematicProfile?.isIntermediary || requestedFrontValidation.length)
        ? validateRpf26Frontespizio(form, telematicProfile)
        : [];
    const computedLmRsErrors = validateRpf26LmRs(form);
    const requestedLmRsValidation = computedLmRsErrors.length || localLmRsErrors.length || errors.some((error) => String(error.field || "").startsWith("quadri.LM.") || String(error.field || "").startsWith("quadri.RS."));
    const lmRsValidationErrors = requestedLmRsValidation ? computedLmRsErrors : [];
    const tabs = RPF26_OFFICIAL_WORKSPACES;
    const specialistCodes = new Set(["FRONT", "LM", "RS", "RN", "RV", "LC", "RX"]);
    const activeSections = specialistCodes.has(active) ? [] : sectionsForRpf26Workspace(other, active);
    useEffect(() => {
        tabRefs.current[active]?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    }, [active]);
    const scrollTabs = (direction) => {
        tabsScrollerRef.current?.scrollBy({ left: direction * 320, behavior: "smooth" });
    };
    return (
        <div data-testid="rpf26-specialist-workspace">
            <nav aria-label="Quadri Redditi PF 2026" data-testid="rpf26-sticky-tabs" className="sticky top-0 z-20 border-b border-orange-300 bg-white/95 px-2 py-2 shadow-sm backdrop-blur">
                <div className="flex items-stretch gap-2">
                    <button type="button" onClick={() => scrollTabs(-1)} aria-label="Scorri quadri a sinistra"
                        className="hidden w-9 shrink-0 items-center justify-center rounded-lg border border-orange-200 bg-white text-orange-700 shadow-sm transition-colors hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-500 md:flex">
                        <ChevronLeft aria-hidden="true" size={20} />
                    </button>
                    <div ref={tabsScrollerRef} data-testid="rpf26-tabs-scroller"
                        className="grid min-w-0 flex-1 auto-cols-max grid-flow-col grid-rows-2 gap-1.5 overflow-x-auto overscroll-x-contain pb-1.5 [scrollbar-gutter:stable] [-webkit-overflow-scrolling:touch]">
                    {tabs.map(([code, title, subtitle]) => {
                        const available = specialistCodes.has(code) || sectionsForRpf26Workspace(other, code).length > 0;
                        return (
                        <button key={code} ref={(element) => { tabRefs.current[code] = element; }} type="button" onClick={() => select(code)}
                            data-testid={`rpf26-tab-${code.toLowerCase()}`} aria-current={active === code ? "page" : undefined}
                            data-available={available ? "true" : "false"}
                            className={`w-32 rounded-md border px-2 py-1.5 text-left transition-colors sm:w-36 ${active === code ? "border-orange-600 bg-orange-600 text-white shadow-sm" : available ? "border-orange-200 bg-orange-50 text-slate-900 hover:bg-orange-100" : "border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100"}`}>
                            <span className="block truncate text-[11px] font-black uppercase tracking-wide">{title}</span>
                            <span className={`block truncate text-[9px] leading-tight ${active === code ? "text-orange-50" : "text-slate-500"}`}>{available ? subtitle : "Non ancora disponibile"}</span>
                        </button>
                    );})}
                    </div>
                    <button type="button" onClick={() => scrollTabs(1)} aria-label="Scorri quadri a destra"
                        className="hidden w-9 shrink-0 items-center justify-center rounded-lg border border-orange-200 bg-white text-orange-700 shadow-sm transition-colors hover:bg-orange-50 focus:outline-none focus:ring-2 focus:ring-orange-500 md:flex">
                        <ChevronRight aria-hidden="true" size={20} />
                    </button>
                </div>
            </nav>
            {active === "FRONT" && <Rpf26EngineWorkspace active="FRONT" form={form} editing={editing} updField={updField} validationErrors={frontValidationErrors} telematicProfileOverride={telematicProfile} />}
            {active === "LM" && <Rpf26EngineWorkspace active="LM" form={form} editing={editing} updField={updField} validationErrors={lmRsValidationErrors} telematicProfileOverride={telematicProfile} />}
            {active === "RS" && <Rpf26EngineWorkspace active="RS" form={form} editing={editing} updField={updField} validationErrors={lmRsValidationErrors} telematicProfileOverride={telematicProfile} />}
            {active === "RN" && <Rpf26RnWorkspace sections={rn} form={form} editing={editing} updField={updField} />}
            {active === "RV" && <section data-testid="rpf26-rv-workspace" className="space-y-4 p-3">
                <header className="rounded-lg border border-orange-200 bg-orange-50 p-4">
                    <h2 className="text-sm font-black uppercase tracking-wide text-slate-900">Quadro RV — Addizionali regionali e comunali all’IRPEF</h2>
                    <p className="mt-1 text-xs text-slate-600">Calcolo, trattenute, eccedenze, debiti, crediti e acconto dell’addizionale comunale.</p>
                </header>
                {rv.map((s) => renderSchemaSection(s, form, editing, updField))}
            </section>}
            {active === "LC" && <section data-testid="rpf26-lc-workspace" className="space-y-4 p-3">
                <header className="rounded-lg border border-orange-200 bg-orange-50 p-4">
                    <h2 className="text-sm font-black uppercase tracking-wide text-slate-900">Quadro LC — Cedolare secca sulle locazioni</h2>
                    <p className="mt-1 text-xs text-slate-600">Liquidazione della cedolare secca, eccedenze, acconti, debito e credito.</p>
                </header>
                {lc.map((s) => renderSchemaSection(s, form, editing, updField))}
            </section>}
            {active === "RX" && <section data-testid="rpf26-rx-workspace" className="space-y-4 p-3">
                <header className="rounded-lg border border-orange-200 bg-orange-50 p-4">
                    <h2 className="text-sm font-black uppercase tracking-wide text-slate-900">Quadro RX — Risultato della dichiarazione</h2>
                    <p className="mt-1 text-xs text-slate-600">Debiti e crediti provengono dai quadri di liquidazione. Restano compilabili soltanto eccedenze e destinazione del credito nei casi previsti.</p>
                </header>
                {rx.map((s) => renderSchemaSection(s, form, editing, updField))}
            </section>}
            {!specialistCodes.has(active) && activeSections.length > 0 && (
                <section data-testid={`rpf26-${active.toLowerCase()}-workspace`} className="space-y-4 p-3">
                    {activeSections.map((s) => renderSchemaSection(s, form, editing, updField))}
                </section>
            )}
            {!specialistCodes.has(active) && activeSections.length === 0 && (
                <section data-testid="rpf26-unavailable-workspace" className="m-3 rounded-lg border border-slate-200 bg-slate-50 p-6 text-center">
                    <h2 className="text-sm font-black uppercase tracking-wide text-slate-800">Quadro {active}</h2>
                    <p className="mt-2 text-sm text-slate-600">Il quadro è presente nel modello ufficiale Redditi PF 2026, ma non è ancora disponibile per la compilazione in FiscoX.</p>
                </section>
            )}
        </div>
    );
}

function Rpf26RnWorkspace({ sections, form, editing, updField }) {
    const grouped = groupRpf26RnFields(sections);
    return (
        <section data-testid="rpf26-rn-workspace" className="space-y-4 bg-slate-50 p-3">
            <header className="rounded-xl border-2 border-orange-400 bg-white p-4 shadow-sm">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                        <div className="text-[10px] font-black uppercase tracking-[0.18em] text-orange-700">Redditi Persone Fisiche 2026</div>
                        <h2 className="mt-1 text-lg font-black uppercase text-slate-950">Quadro RN — Determinazione dell’IRPEF</h2>
                        <p className="mt-1 max-w-3xl text-xs leading-relaxed text-slate-600">I risultati con etichetta “Motore fiscale” sono ricalcolati dal server e non sono modificabili. Sono editabili soltanto i dati documentali previsti dal rigo ufficiale.</p>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-center">
                        <RnCounter label="Campi ufficiali" value={grouped.officialCount} testid="rpf26-rn-official-count" />
                        <RnCounter label="Calcolati" value={grouped.engineCount} />
                        <RnCounter label="Documentali" value={grouped.documentInputCount} />
                    </div>
                </div>
                {grouped.officialCount !== 150 && (
                    <div role="alert" className="mt-3 rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs font-bold text-red-800">
                        Schema RN incompleto: attesi 150 campi ufficiali, disponibili {grouped.officialCount}.
                    </div>
                )}
            </header>

            {grouped.bands.map((band, index) => (
                <details key={band.code} open={index === 0 || band.code === "RN33_RN46"} className="overflow-hidden rounded-xl border border-slate-300 bg-white shadow-sm" data-testid={`rpf26-rn-band-${band.code.toLowerCase()}`}>
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-3 bg-slate-900 px-4 py-3 text-white">
                        <span className="text-xs font-black uppercase tracking-wide">{band.title}</span>
                        <span className="rounded bg-orange-500 px-2 py-1 text-[10px] font-bold">{band.subtitle} · {band.rows.reduce((count, row) => count + row.fields.length, 0)} campi</span>
                    </summary>
                    <div className="divide-y divide-slate-200">
                        {band.rows.map((row) => (
                            <div key={row.code} className="grid gap-3 p-4 lg:grid-cols-[90px_minmax(0,1fr)]" data-testid={`rpf26-rn-row-${row.code.toLowerCase()}`}>
                                <div className="flex items-start justify-between lg:block">
                                    <div className="inline-flex rounded-md border border-orange-300 bg-orange-50 px-2.5 py-1 text-sm font-black text-orange-900">{row.code}</div>
                                    <div className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">{row.fields.length} {row.fields.length === 1 ? "campo" : "campi"}</div>
                                </div>
                                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                                    {row.fields.map((field) => <Rpf26RnField key={field.official_code} field={field} form={form} editing={editing} updField={updField} />)}
                                </div>
                            </div>
                        ))}
                    </div>
                </details>
            ))}

            {grouped.supportFields.length > 0 && (
                <details className="overflow-hidden rounded-xl border border-slate-300 bg-white" data-testid="rpf26-rn-support-fields">
                    <summary className="cursor-pointer bg-slate-100 px-4 py-3 text-xs font-black uppercase tracking-wide text-slate-800">Dati di supporto non telematici</summary>
                    <div className="grid gap-3 p-4 sm:grid-cols-2 xl:grid-cols-3">
                        {grouped.supportFields.map((field) => <Rpf26RnField key={field.path} field={field} form={form} editing={editing} updField={updField} support />)}
                    </div>
                </details>
            )}
        </section>
    );
}

function RnCounter({ label, value, testid }) {
    return <div className="min-w-[74px] rounded-lg bg-orange-50 px-2 py-2" data-testid={testid}><div className="text-lg font-black leading-none text-orange-800">{value}</div><div className="mt-1 text-[9px] font-bold uppercase tracking-wide text-slate-600">{label}</div></div>;
}

function Rpf26RnField({ field, form, editing, updField, support = false }) {
    const engineOwned = field.field_owner === "fiscal_engine";
    const value = getPath(form, field.path);
    const label = String(field.label || "").replace(/^RN\d+(?:\s+col\.\d+)?\s*[—-]\s*/, "");
    return (
        <div className={`rounded-lg border p-3 ${engineOwned ? "border-slate-200 bg-slate-50" : "border-orange-300 bg-orange-50/60"}`} data-testid={`rpf26-rn-field-${field.official_code || field.key}`}>
            <div className="mb-2 flex items-start justify-between gap-2">
                <div>
                    {field.official_code && <div className="font-mono text-[9px] font-bold tracking-wide text-slate-500">{field.official_code}</div>}
                    <div className="mt-0.5 text-[10px] font-bold uppercase leading-snug tracking-wide text-slate-800">{label}</div>
                </div>
                <span className={`shrink-0 rounded px-1.5 py-1 text-[8px] font-black uppercase tracking-wide ${engineOwned ? "bg-slate-800 text-white" : "bg-orange-600 text-white"}`}>
                    {engineOwned ? "Motore fiscale" : support ? "Supporto" : "Dato documentale"}
                </span>
            </div>
            {editing && !engineOwned && !field.readonly ? (
                <FieldInput field={{ ...field, testid: `rpf26-rn-input-${field.official_code || field.key}` }} path={field.path} value={value} onChange={(next) => updField(field.path, next)} updField={updField} compact />
            ) : (
                <div aria-readonly="true" className="min-h-[34px] rounded-md border border-slate-200 bg-white px-2.5 py-2 text-sm font-semibold text-slate-900">
                    <V value={displayValue(field, value)} />
                </div>
            )}
        </div>
    );
}

function normalizeListRows(value) {
    if (Array.isArray(value)) return { rows: value, nestedKey: null };
    if (!value || typeof value !== "object") return { rows: [], nestedKey: null };

    // Imported official declarations may group the actual repeated rows under
    // a named child (for example FAM.familiari or RB.righi). Prefer arrays of
    // row objects, then any available array, without mutating the source form.
    const arrays = Object.entries(value).filter(([, child]) => Array.isArray(child));
    const rowEntry = arrays.find(([, items]) => items.some((item) => item && typeof item === "object" && !Array.isArray(item)));
    if (rowEntry) return { rows: rowEntry[1], nestedKey: rowEntry[0] };
    if (arrays.length === 1) return { rows: arrays[0][1], nestedKey: arrays[0][0] };

    // A single imported object is still renderable as one row. This avoids a
    // full React crash while preserving data that has not yet been normalized.
    return { rows: [value], nestedKey: null };
}

function fieldPath(section, field) {
    if (field.path) return field.path;
    return section.path ? `${section.path}.${field.key}` : field.key;
}

function SingleSection({ section, form, editing, updField }) {
    const fields = section.fields || [];
    const isRpfFront = section.code === "FRONT" || String(section.code || "").startsWith("FRONT-");
    return (
        <>
        {section.code === "FRONT" && (
            <div data-testid="rpf26-frontespizio-header" className="mx-4 mt-4 rounded-lg border-2 border-orange-400 bg-gradient-to-r from-orange-50 to-white px-5 py-4 flex items-center justify-between gap-4">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-orange-700">Modello Redditi Persone Fisiche</div>
                    <div className="mt-1 text-xl font-black uppercase text-slate-900">Frontespizio RPF 2026</div>
                    <div className="mt-1 text-xs text-slate-600">Periodo d’imposta 2025 · struttura ufficiale Agenzia delle Entrate</div>
                </div>
                <div className="shrink-0 rounded-md bg-orange-600 px-4 py-3 text-center text-white shadow-sm">
                    <div className="text-2xl font-black leading-none">PF</div>
                    <div className="mt-1 text-[10px] font-bold tracking-widest">2026</div>
                </div>
            </div>
        )}
        <Section title={section.title} notes={section.notes} official={isRpfFront}>
            {fields
                .filter((f) => evalShowIf(f.showIf, form))
                .map((field) => {
                    const path = fieldPath(section, field);
                    return (
                        <Row key={path} label={field.label + (field.required ? " *" : "")}>
                            {editing ? (
                                <FieldInput field={field} path={path} value={getPath(form, path)} onChange={(v) => updField(path, v)} updField={updField} />
                            ) : (
                                <V value={displayValue(field, getPath(form, path))} />
                            )}
                        </Row>
                    );
                })}
        </Section>
        </>
    );
}

function ListSection({ section, form, editing, updField }) {
    const rawValue = getPath(form, section.path);
    const normalized = normalizeListRows(rawValue);
    const rows = normalized.rows;
    const cols = Array.isArray(section.columns)
        ? section.columns
        : (Array.isArray(section.fields) ? section.fields : []);
    if (!editing && rows.length === 0) {
        return (
            <div className="border-b border-border last:border-b-0">
                <div className="px-5 py-2.5 bg-background-alt/50 text-[11px] uppercase tracking-[0.12em] font-semibold text-muted-foreground">{section.title}</div>
                <div className="px-5 py-3 text-xs text-muted-foreground">Nessuna riga compilata</div>
            </div>
        );
    }
    const setRows = (next) => updField(
        normalized.nestedKey ? `${section.path}.${normalized.nestedKey}` : section.path,
        next,
    );
    const addRow = () => setRows([...rows, {}]);
    const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
    const updCell = (i, key, v) => {
        const next = rows.map((r, idx) => (idx === i ? { ...r, [key]: v } : r));
        setRows(next);
    };

    return (
        <div className="border-b border-border last:border-b-0">
            <div className="px-5 py-2.5 bg-background-alt/50 text-[11px] uppercase tracking-[0.12em] font-semibold text-muted-foreground flex items-center justify-between">
                <span>{section.title}</span>
                {editing && (
                    <button
                        type="button"
                        data-testid={`add-row-${section.code || section.path}`}
                        onClick={addRow}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-semibold bg-primary/10 text-primary hover:bg-primary/20 transition-colors normal-case tracking-normal"
                    >
                        <Plus size={11} /> {section.item_label || "Riga"}
                    </button>
                )}
            </div>
            <div className="px-5 py-4 overflow-x-auto">
                {rows.length === 0 ? (
                    <div className="text-xs text-muted-foreground">Nessuna riga. {editing ? "Aggiungi la prima riga." : ""}</div>
                ) : (
                    <table className="w-full text-sm min-w-[600px]">
                        <thead>
                            <tr>
                                {cols.map((c) => (
                                    <th key={c.key} className="text-start pb-2 pe-3 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">{c.label}</th>
                                ))}
                                {editing && <th className="w-8"></th>}
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row, i) => (
                                <tr key={i} className="border-t border-border/60">
                                    {cols.map((c) => (
                                        <td key={c.key} className="py-1.5 pe-3 align-top">
                                            {editing ? (
                                                <FieldInput field={c} path={`${section.path}.${i}.${c.key}`} value={row?.[c.key]} onChange={(v) => updCell(i, c.key, v)} updField={updField} compact />
                                            ) : (
                                                <V value={displayValue(c, row?.[c.key])} />
                                            )}
                                        </td>
                                    ))}
                                    {editing && (
                                        <td className="py-1.5">
                                            <button type="button" onClick={() => removeRow(i)} className="text-muted-foreground hover:text-orange-600 transition-colors p-1">
                                                <Trash2 size={13} />
                                            </button>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function displayValue(field, v) {
    if (field.type === "boolean") return v === true ? "Sì" : v === false ? "No" : "";
    if (field.type === "select" && Array.isArray(field.options)) {
        const opt = field.options.find((o) => (typeof o === "object" ? o.value : o) === v);
        if (opt) return typeof opt === "object" ? opt.label : opt;
    }
    if (v && typeof v === "object") {
        try { return JSON.stringify(v); } catch { return "Dato strutturato"; }
    }
    return v;
}

function FieldInput({ field, path, value, onChange, updField, compact }) {
    const cls = compact ? inputCls + " !py-1.5 !px-2 text-xs" : inputCls;
    const common = { "data-testid": field.testid, className: cls, disabled: Boolean(field.readonly) };
    const smartPaths = path === "frontespizio.codice_fiscale" || path in {
        "frontespizio.comune_nascita": 1, "frontespizio.residenza_comune": 1,
        "frontespizio.domicilio_fiscale_comune": 1, "frontespizio.domicilio_fiscale_2026_comune": 1,
        "frontespizio.rappresentante_comune_residenza": 1, "frontespizio.estero_stato": 1,
        "frontespizio.rappresentante_stato_estero": 1,
    } || field.key === "ateco_2025" || field.key === "codice_attivita";
    if (smartPaths) return <Rpf26SmartInput field={field} path={path} value={value} onChange={onChange} updField={updField} compact={compact} />;
    if (field.type === "checkbox") {
        return (
            <input
                {...common}
                className="h-4 w-4 rounded border-border accent-primary disabled:opacity-60"
                type="checkbox"
                checked={Boolean(value)}
                onChange={(e) => onChange(e.target.checked)}
            />
        );
    }
    if (field.type === "boolean") {
        return (
            <select
                {...common}
                value={value === true ? "true" : value === false ? "false" : ""}
                onChange={(e) => onChange(e.target.value === "" ? null : e.target.value === "true")}
            >
                <option value="">— Seleziona —</option>
                <option value="true">Sì</option>
                <option value="false">No</option>
            </select>
        );
    }
    if (field.type === "select") {
        const options = Array.isArray(field.options) ? field.options : [];
        return (
            <select {...common} value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
                <option value="">—</option>
                {options.map((o) => (
                    <option key={typeof o === "object" ? o.value : o} value={typeof o === "object" ? o.value : o}>{typeof o === "object" ? o.label : o}</option>
                ))}
            </select>
        );
    }
    if (field.type === "date") {
        return <input {...common} type="date" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
    }
    if (field.type === "number") {
        return <input {...common} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : parseFloat(e.target.value))} />;
    }
    if (field.type === "textarea") {
        return <textarea {...common} rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
    }
    return (
        <input
            {...common}
            value={value || ""}
            maxLength={field.maxLength}
            placeholder={field.placeholder}
            onChange={(e) => onChange(field.upper ? e.target.value.toUpperCase() : e.target.value)}
        />
    );
}

export function Section({ title, notes, official = false, children }) {
    return (
        <div className={`border-b border-border last:border-b-0 ${official ? "mx-4 border-x border-orange-200" : ""}`}>
            <div className={`px-5 py-2.5 text-[11px] uppercase tracking-[0.12em] font-semibold ${official ? "bg-orange-100 text-orange-950 border-t border-orange-200" : "bg-background-alt/50 text-muted-foreground"}`}>{title}</div>
            {notes && <div className="px-5 pt-3 text-[11px] leading-relaxed text-muted-foreground">{notes}</div>}
            <div className="px-5 py-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {children}
            </div>
        </div>
    );
}

export function Row({ label, children }) {
    return (
        <div>
            <div className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">{label}</div>
            {children}
        </div>
    );
}

export function V({ value }) {
    return <div className="text-sm font-medium text-foreground min-h-[20px]">{value || value === 0 ? String(value) : <span className="text-muted-foreground">—</span>}</div>;
}
